/* =========================================================================
   SAMSARA — COLLECTION-GRID.JS
   Sprint 4 / T3 + T4 + post-Sprint-4 fetch enhancement.

   Progressive enhancement:
   - Forms submit natively (works no JS).
   - Sort onChange → fetch + replace (no full reload).
   - Filter panel Apply → fetch + replace (no full reload).
   - Active chip × click → fetch + replace (no full reload).
   - Browser back/forward (popstate) → re-fetch state.
   - Filter panel toggle expand/collapse + Esc collapse + focus return.
   ========================================================================= */

(function () {
  'use strict';

  const FETCH_TIMEOUT_MS = 8000;
  let currentAbort = null;

  function debounce(fn, wait) {
    let t;
    return function () {
      const args = arguments;
      const ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function fetchAndReplace(url, options) {
    if (currentAbort) currentAbort.abort();
    currentAbort = new AbortController();

    const root = document.querySelector('[data-collection-grid]');
    if (!root) {
      window.location.href = url;
      return;
    }

    root.classList.add('is-loading');
    root.setAttribute('aria-busy', 'true');

    const timeoutId = setTimeout(() => currentAbort && currentAbort.abort(), FETCH_TIMEOUT_MS);

    fetch(url, {
      signal: currentAbort.signal,
      headers: { 'Accept': 'text/html' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        clearTimeout(timeoutId);

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newRoot = doc.querySelector('[data-collection-grid]');
        if (!newRoot) throw new Error('No grid in response');

        // Replace inner content (preserve outer section element + data attrs).
        root.innerHTML = newRoot.innerHTML;

        // Update URL + browser title fără full reload.
        if (!options || !options.skipPushState) {
          history.pushState({ collectionUrl: url }, '', url);
        }
        if (doc.title) document.title = doc.title;

        // Re-init JS handlers pe new DOM (chips, panel toggle, sort).
        initBindings(root);

        // Smooth scroll up to bar when filters change.
        const bar = root.querySelector('[data-collection-bar]');
        if (bar) {
          const top = bar.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo({ top, behavior: 'smooth' });
        }

        // Dispatch event for analytics / other listeners.
        document.dispatchEvent(new CustomEvent('collection:loaded', {
          detail: { url: url }
        }));
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        // Fallback graceful: full page navigate.
        window.location.href = url;
      })
      .finally(function () {
        root.classList.remove('is-loading');
        root.removeAttribute('aria-busy');
      });
  }

  function initBindings(root) {
    if (!root) return;

    /* --- Filter panel toggle (re-bind after replace) --- */
    const toggleBtn = root.querySelector('[data-collection-filter-toggle]');
    const panel = root.querySelector('[data-collection-filter-panel]');

    if (toggleBtn && panel) {
      toggleBtn.addEventListener('click', function () {
        const isOpen = !panel.hasAttribute('hidden');
        if (isOpen) {
          panel.setAttribute('hidden', '');
          toggleBtn.setAttribute('aria-expanded', 'false');
        } else {
          panel.removeAttribute('hidden');
          toggleBtn.setAttribute('aria-expanded', 'true');
        }
      });

      panel.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          panel.setAttribute('hidden', '');
          toggleBtn.setAttribute('aria-expanded', 'false');
          toggleBtn.focus();
        }
      });
    }

    /* --- Sort dropdown — fetch on change --- */
    const sortSelect = root.querySelector('[data-collection-sort]');
    const sortForm = root.querySelector('[data-collection-sort-form]');

    if (sortSelect && sortForm) {
      sortSelect.addEventListener('change', function () {
        const params = new URLSearchParams(new FormData(sortForm));
        const url = sortForm.action + '?' + params.toString();
        fetchAndReplace(url);
      });
    }

    /* --- Filter panel form — fetch on Apply submit --- */
    if (panel && panel.tagName === 'FORM') {
      panel.addEventListener('submit', function (e) {
        e.preventDefault();
        const params = new URLSearchParams(new FormData(panel));
        // Strip empty values + group multi-select properly.
        const url = panel.action + '?' + params.toString();
        fetchAndReplace(url);
      });
    }

    /* --- Active chip click → fetch instead of full nav --- */
    root.addEventListener('click', function (e) {
      const chip = e.target.closest('[data-collection-chip]');
      if (chip) {
        e.preventDefault();
        fetchAndReplace(chip.href);
        return;
      }
      const clearLink = e.target.closest('.collection-grid__chip-clear, .collection-grid__panel-clear');
      if (clearLink && clearLink.href) {
        e.preventDefault();
        fetchAndReplace(clearLink.href);
        return;
      }
      // Pagination links also through fetch.
      const pageLink = e.target.closest('.collection-grid__page-link[href], .collection-grid__page-num[href]');
      if (pageLink && pageLink.href) {
        e.preventDefault();
        fetchAndReplace(pageLink.href);
        return;
      }
    });
  }

  function onPopState(e) {
    if (e.state && e.state.collectionUrl) {
      fetchAndReplace(window.location.href, { skipPushState: true });
    } else if (window.location.pathname.indexOf('/collections/') === 0) {
      fetchAndReplace(window.location.href, { skipPushState: true });
    }
  }

  function init() {
    const grid = document.querySelector('[data-collection-grid]');
    if (!grid) return;
    initBindings(grid);
    window.addEventListener('popstate', onPopState);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
